/**
 * VÉRAULT Travels - Destination Segments Form
 * 
 * Multi-destination support with Google Places Autocomplete.
 * Each segment has: name, place_id, lat/lng, timezone, dates
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Trash2, MapPin, Calendar, GripVertical, ChevronDown, ChevronUp,
  Search, Loader2, Globe, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: Parse date as local to avoid timezone issues
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const parts = dateStr.split('T')[0].split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return new Date(dateStr);
}

// ============================================================
// GOOGLE PLACES AUTOCOMPLETE INPUT
// ============================================================
function PlaceAutocompleteInput({ value, onChange, onSelect, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const searchPlaces = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/places/autocomplete`, {
        params: { query: searchQuery, types: '(cities)' }
      });
      setSuggestions(res.data?.predictions || []);
    } catch (err) {
      console.error('Places search failed:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    onChange?.(newQuery);

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchPlaces(newQuery);
    }, 300);
  };

  const handleSelectPlace = async (prediction) => {
    setQuery(prediction.description);
    onChange?.(prediction.description);
    setSuggestions([]);
    setShowDropdown(false);

    // Fetch place details
    try {
      const res = await axios.get(`${API}/places/details/${prediction.place_id}`);
      const result = res.data?.result || {};
      
      onSelect?.({
        destination_name: prediction.description,
        google_place_id: prediction.place_id,
        latitude: result.geometry?.location?.lat,
        longitude: result.geometry?.location?.lng,
        timezone: result.timezone,
        country: result.address_components?.find(c => c.types.includes('country'))?.long_name,
        country_code: result.address_components?.find(c => c.types.includes('country'))?.short_name
      });
    } catch (err) {
      console.error('Failed to get place details:', err);
      onSelect?.({
        destination_name: prediction.description,
        google_place_id: prediction.place_id
      });
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
        <Input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder={placeholder || "Search destination..."}
          className="pl-9 pr-8 bg-[#F5F1EB]"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C8A85A] animate-spin" />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#E6E4DE] rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((prediction, idx) => (
            <button
              key={prediction.place_id || idx}
              type="button"
              onClick={() => handleSelectPlace(prediction)}
              className="w-full text-left px-4 py-3 hover:bg-[#F5F1EB] border-b border-[#E6E4DE] last:border-b-0 transition-colors"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#C8A85A] flex-shrink-0" />
                <span className="text-sm text-[#1A1A1A]">{prediction.description}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// SINGLE DESTINATION SEGMENT ROW
// ============================================================
function DestinationSegmentRow({ 
  segment, 
  index, 
  onUpdate, 
  onDelete, 
  tripStartDate, 
  tripEndDate,
  isExpanded,
  onToggleExpand 
}) {
  const [localSegment, setLocalSegment] = useState(segment);

  useEffect(() => {
    setLocalSegment(segment);
  }, [segment]);

  const handleFieldChange = (field, value) => {
    const updated = { ...localSegment, [field]: value };
    setLocalSegment(updated);
    onUpdate(updated);
  };

  const handlePlaceSelect = (placeData) => {
    const updated = { ...localSegment, ...placeData };
    setLocalSegment(updated);
    onUpdate(updated);
  };

  return (
    <div className="bg-white rounded-xl border border-[#E6E4DE] overflow-hidden">
      {/* Header - Always visible */}
      <div 
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-[#F9F8F4] transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-2 text-[#8C8C8C]">
          <GripVertical className="w-4 h-4" />
          <span className="text-xs font-medium w-6">{index + 1}</span>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#C8A85A]" />
            <span className="font-medium text-[#1A1A1A] truncate">
              {localSegment.destination_name || 'New Destination'}
            </span>
            {localSegment.timezone && (
              <span className="text-xs text-[#8C8C8C] flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {localSegment.timezone}
              </span>
            )}
          </div>
          {localSegment.segment_start_date && localSegment.segment_end_date && (
            <p className="text-xs text-[#8C8C8C] mt-1">
              {parseLocalDate(localSegment.segment_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {parseLocalDate(localSegment.segment_end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 p-2"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#8C8C8C]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#8C8C8C]" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4 border-t border-[#E6E4DE] bg-[#F9F8F4]">
          {/* Destination Search */}
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Destination</label>
            <PlaceAutocompleteInput
              value={localSegment.destination_name}
              onChange={(val) => handleFieldChange('destination_name', val)}
              onSelect={handlePlaceSelect}
              placeholder="Search for a city..."
            />
          </div>

          {/* Dates - Side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Arrival Date</label>
              <Input
                type="date"
                value={localSegment.segment_start_date || ''}
                onChange={(e) => handleFieldChange('segment_start_date', e.target.value)}
                min={tripStartDate}
                max={tripEndDate}
                className="bg-white"
              />
            </div>
            <div>
              <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Departure Date</label>
              <Input
                type="date"
                value={localSegment.segment_end_date || ''}
                onChange={(e) => handleFieldChange('segment_end_date', e.target.value)}
                min={localSegment.segment_start_date || tripStartDate}
                max={tripEndDate}
                className="bg-white"
              />
            </div>
          </div>

          {/* Location Info (if resolved) */}
          {(localSegment.latitude || localSegment.country) && (
            <div className="flex flex-wrap gap-3 text-xs text-[#8C8C8C] pt-2 border-t border-[#E6E4DE]">
              {localSegment.country && (
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {localSegment.country}
                </span>
              )}
              {localSegment.latitude && localSegment.longitude && (
                <span>
                  {localSegment.latitude.toFixed(4)}, {localSegment.longitude.toFixed(4)}
                </span>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs text-[#8C8C8C] uppercase mb-1 block">Notes (Optional)</label>
            <Input
              value={localSegment.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              placeholder="Any notes for this destination..."
              className="bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN DESTINATION SEGMENTS FORM
// ============================================================
export function DestinationSegmentsForm({ 
  segments = [], 
  onChange, 
  tripStartDate, 
  tripEndDate,
  tripId 
}) {
  const [localSegments, setLocalSegments] = useState(segments);
  const [expandedIndex, setExpandedIndex] = useState(segments.length > 0 ? 0 : null);

  useEffect(() => {
    setLocalSegments(segments);
  }, [segments]);

  const handleAddSegment = () => {
    const newSegment = {
      id: `temp_${Date.now()}`,
      destination_name: '',
      google_place_id: null,
      latitude: null,
      longitude: null,
      timezone: null,
      segment_start_date: '',
      segment_end_date: '',
      country: null,
      country_code: null,
      notes: null
    };
    
    const updated = [...localSegments, newSegment];
    setLocalSegments(updated);
    onChange?.(updated);
    setExpandedIndex(updated.length - 1);
  };

  const handleUpdateSegment = (index, updatedSegment) => {
    const updated = localSegments.map((seg, i) => 
      i === index ? updatedSegment : seg
    );
    setLocalSegments(updated);
    onChange?.(updated);
  };

  const handleDeleteSegment = (index) => {
    const updated = localSegments.filter((_, i) => i !== index);
    setLocalSegments(updated);
    onChange?.(updated);
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else if (expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  // Calculate travel days
  const travelDaysCount = calculateTravelDays(tripStartDate, tripEndDate, localSegments);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#C8A85A]" />
            Destinations
          </h3>
          <p className="text-xs text-[#8C8C8C] mt-1">
            {localSegments.length} destination{localSegments.length !== 1 ? 's' : ''}
            {travelDaysCount > 0 && ` • ${travelDaysCount} travel day${travelDaysCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddSegment}
          className="border-[#C8A85A] text-[#C8A85A] hover:bg-[#C8A85A]/10"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Destination
        </Button>
      </div>

      {/* Segments List */}
      <div className="space-y-3">
        {localSegments.length === 0 ? (
          <div className="text-center py-8 bg-[#F5F1EB] rounded-xl border border-dashed border-[#E6E4DE]">
            <MapPin className="w-8 h-8 text-[#E6E4DE] mx-auto mb-2" />
            <p className="text-sm text-[#8C8C8C]">No destinations added yet</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddSegment}
              className="mt-2 text-[#C8A85A]"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add your first destination
            </Button>
          </div>
        ) : (
          localSegments.map((segment, index) => (
            <DestinationSegmentRow
              key={segment.id || index}
              segment={segment}
              index={index}
              onUpdate={(updated) => handleUpdateSegment(index, updated)}
              onDelete={() => handleDeleteSegment(index)}
              tripStartDate={tripStartDate}
              tripEndDate={tripEndDate}
              isExpanded={expandedIndex === index}
              onToggleExpand={() => setExpandedIndex(expandedIndex === index ? null : index)}
            />
          ))
        )}
      </div>

      {/* Travel Days Info */}
      {travelDaysCount > 0 && (
        <div className="p-3 bg-[#F5F1EB] rounded-lg border border-[#E6E4DE]">
          <div className="flex items-center gap-2 text-sm text-[#595959]">
            <Calendar className="w-4 h-4 text-[#C8A85A]" />
            <span className="font-medium">Travel Days Detected:</span>
            <span>{travelDaysCount} day{travelDaysCount !== 1 ? 's' : ''} between destinations</span>
          </div>
          <p className="text-xs text-[#8C8C8C] mt-1 ml-6">
            These will appear in your itinerary as "Travel Day" entries.
          </p>
        </div>
      )}
    </div>
  );
}

// Helper function to calculate travel days
function calculateTravelDays(startDate, endDate, segments) {
  if (!startDate || !endDate) return 0;
  
  try {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    if (!start || !end) return 0;
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    
    // Count days covered by segments
    let coveredDays = 0;
    for (const segment of segments) {
      if (segment.segment_start_date && segment.segment_end_date) {
        const segStart = parseLocalDate(segment.segment_start_date);
        const segEnd = parseLocalDate(segment.segment_end_date);
        if (segStart && segEnd) {
          coveredDays += Math.ceil((segEnd - segStart) / (1000 * 60 * 60 * 24)) + 1;
        }
      }
    }
    
    return Math.max(0, totalDays - coveredDays);
  } catch {
    return 0;
  }
}

export default DestinationSegmentsForm;
